import React from 'react';
import dom from 'react-dom/client';

import {iframeCx} from "./wireframe-editor";
import {Notification, Popup} from "../components/popups";

export const colourSlots = [
	'--wf-bg',
	'--wf-surface',
	'--wf-surface-alt',
	'--wf-border',
	'--wf-text',
	'--wf-text-muted',
	'--wf-primary',
	'--wf-primary-hover',
	'--wf-on-primary',
	'--wf-accent',
	'--wf-on-accent',
	'--wf-header-bg',
	'--wf-footer-bg',
	'--wf-placeholder',
] as const;

type ColourSlot = typeof colourSlots[number];

// Loosened from the old template-literal union: computed values legitimately come back as
// `color(srgb …)`, and the union silently failed to catch the bad default anyway.
type Palette = Record<ColourSlot, string>;

export default function ColourButton() {
	const getIframe = React.useContext(iframeCx)!;

	const openEditor = () => Popup({
		id: 'colour-palette-editor',
		children: <>
			{/* @ts-ignore */}
			<button className="symbolic close-btn" data-icon={"\ue5cd"} command="request-close"
			        commandfor="colour-palette-editor"/>

			<PaletteEditor iframe={getIframe()} />
		</>
	});

	return <button onClick={() => openEditor()} data-icon={'\ue40a'}>
		Farbschema Bearbeiten
	</button>
}

function isSignificantlyDefined(colours: Palette): boolean {
	let num = 0;

	for (const colour in colours)
		if (colours[colour as keyof typeof colours])
			num++;

	return num / Object.keys(colours).length > 0.25;
}

function useManagedStylesheet(doc: Document, id: string): HTMLStyleElement | null {
	const [styleEl, setStyleEl] = React.useState<HTMLStyleElement | null>(null);

	React.useEffect(() => {
		let el = doc.getElementById(id) as HTMLStyleElement | null;
		if (!el) {
			el = doc.createElement('style');
			el.id = id;
			doc.head.appendChild(el);
		}
		setStyleEl(el);
	}, [doc, id]);

	return styleEl;
}

// Maps a semantic slot to the extracted colour variable it was linked to, so the
// unlink button can tell "dropped from the palette" from "picked by hand".
type ColourOverride = Partial<Record<ColourSlot, `--colour-${number}`>>;

/* ------------------------------------------------------------------ *
 * Colour parsing / serialisation
 *
 * Internal representation is always 0–1 per channel, alpha 0–1.
 * The 0–255 scale exists only inside parseColour and serialiseColour.
 * ------------------------------------------------------------------ */

type Rgb = { r: number, g: number, b: number, a?: number };

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

// scale applies only to plain numbers; percentages are always /100
const num = (v: string, scale = 1) =>
	v.endsWith('%') ? parseFloat(v) / 100 : Number(v) / scale;

const parseColour = (colour: string): Rgb => {
	const src = colour.trim();

	// #rgb / #rgba / #rrggbb / #rrggbbaa — needed because <input type="color"> hands back hex
	const hex = src.match(/^#([0-9a-f]+)$/i)?.[1];
	if (hex && [3, 4, 6, 8].includes(hex.length)) {
		const step = hex.length < 6 ? 1 : 2;
		const chan = (i: number) => {
			const part = hex.slice(i * step, i * step + step);
			return parseInt(step === 1 ? part + part : part, 16) / 255;
		};
		const [r, g, b] = [0, 1, 2].map(chan);
		return hex.length === 4 || hex.length === 8 ? {r, g, b, a: chan(3)} : {r, g, b};
	}

	const legacy = src.match(/^rgba?\(([^)]+)\)$/i);
	const modern = src.match(/^color\(\s*srgb\s+([^)]+)\)$/i);

	const [body, scale] = legacy ? [legacy[1], 255] : modern ? [modern[1], 1] : [null, 1];
	if (body === null) throw new Error(`Invalid colour ${colour}`);

	const parts = body.split(/[\s,\/]+/).filter(Boolean);
	const [r, g, b] = parts.slice(0, 3).map(v => num(v, scale));
	const a = parts[3] === undefined ? undefined : num(parts[3]); // 0–1 or %

	return a === undefined ? {r, g, b} : {r, g, b, a};
};

const serialiseColour = ({r, g, b, a}: Rgb): string => {
	// Cheap tripwire for the "multiplied by 255 twice" class of bug, which is otherwise silent.
	if (r > 1 || g > 1 || b > 1)
		throw new Error(`serialiseColour expects normalised 0–1 channels, got ${r} ${g} ${b}`);

	const [R, G, B] = [r, g, b].map(c => Math.round(clamp01(c) * 255));
	return a === undefined || a >= 1 ? `rgb(${R} ${G} ${B})` : `rgb(${R} ${G} ${B} / ${a})`;
};

const hex2 = (n: number) => Math.round(clamp01(n) * 255).toString(16).padStart(2, '0');

// <input type="color"> only accepts #rrggbb and silently resets to black on anything else.
const toSwatch = (colour: string): string => {
	if (!colour) return '#ffffff';
	try {
		const {r, g, b} = parseColour(colour);
		return `#${hex2(r)}${hex2(g)}${hex2(b)}`;
	} catch {
		return '#ffffff';
	}
};

// sRGB -> Oklab (Björn Ottosson's formulas). Channels are 0–1.
function srgbToOklab(r: number, g: number, b: number) {
	const toLinear = (c: number) => c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
	const [lr, lg, lb] = [toLinear(r), toLinear(g), toLinear(b)];

	const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
	const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
	const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

	const l_ = Math.cbrt(l), m_ = Math.cbrt(m), s_ = Math.cbrt(s);

	return {
		L: 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
		a: 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
		b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
	};
}

interface Colour {
	raw: string,
	rgb: Rgb,
}

interface AnalysedColour extends Colour {
	oklab: { L: number, a: number, b: number },
	chroma: number,
	projection: number,
}

function sortByThemeAxis(
	colours: Colour[],
	{lightnessBuckets = 8, chromaThreshold = 0.01} = {},
): AnalysedColour[] {
	if (!colours.length) return [];

	const lab: AnalysedColour[] = colours.map(c => {
		const oklab = srgbToOklab(c.rgb.r, c.rgb.g, c.rgb.b);
		return {...c, oklab, chroma: Math.hypot(oklab.a, oklab.b), projection: 0};
	});

	// Chroma-weighted second-moment tensor rather than a vector sum: squaring means
	// antipodal hues (blue + orange) reinforce the same axis instead of cancelling out.
	let mAA = 0, mAB = 0, mBB = 0;
	for (const c of lab) {
		if (c.chroma < chromaThreshold) continue;
		mAA += c.chroma * c.oklab.a * c.oklab.a;
		mAB += c.chroma * c.oklab.a * c.oklab.b;
		mBB += c.chroma * c.oklab.b * c.oklab.b;
	}

	let axA = 1, axB = 0;
	if (mAA + mBB > 0) {
		// Principal eigenvector of [[mAA, mAB], [mAB, mBB]].
		const theta = 0.5 * Math.atan2(2 * mAB, mAA - mBB);
		axA = Math.cos(theta);
		axB = Math.sin(theta);

		// Eigenvectors are sign-ambiguous; anchor so + points toward the most saturated colour.
		const anchor = lab.reduce((best, c) => c.chroma > best.chroma ? c : best, lab[0]);
		if (anchor.oklab.a * axA + anchor.oklab.b * axB < 0) {
			axA = -axA;
			axB = -axB;
		}
	}

	for (const c of lab)
		c.projection = c.oklab.a * axA + c.oklab.b * axB; // signed: + toward theme colour, - away

	// Clamp so pure white doesn't land in a bucket of its own beyond the requested count.
	const bucket = (L: number) =>
		Math.min(lightnessBuckets - 1, Math.max(0, Math.floor(L * lightnessBuckets)));

	return lab.toSorted((x, y) => {
		const bx = bucket(x.oklab.L), by = bucket(y.oklab.L);
		if (bx !== by) return bx - by;
		if (Math.abs(x.projection - y.projection) > 1e-4) return x.projection - y.projection;
		return x.oklab.L - y.oklab.L;
	});
}

function sortColours(input: Iterable<string>): string[] {
	const parsed: Colour[] = [];
	const unparsed: string[] = [];

	// One unrecognised syntax (oklch, lab, a wide-gamut color()) shouldn't take down
	// the whole extraction — park it at the end of the palette instead.
	for (const raw of input) {
		try {
			parsed.push({raw, rgb: parseColour(raw)});
		} catch {
			console.warn(`Colour left unsorted, no parser for: ${raw}`);
			unparsed.push(raw);
		}
	}

	return [...sortByThemeAxis(parsed).map(i => i.raw), ...unparsed];
}

// TODO: Make dropped colour apply to all elements directly rather than through a proxy variable.
// TODO: Implement IndexedDB storage and undo/redo

interface ExtractedColoursProps {
	extracted: Record<string, string>;
	onExtracted: (colours: Record<string, string>) => void;
	iframe: HTMLIFrameElement
}

function ExtractedColours(props: ExtractedColoursProps) {
	const [doc, win] = React.useMemo(() => [props.iframe.contentDocument!, props.iframe.contentWindow! as typeof window], [props.iframe.contentDocument, props.iframe.contentWindow]);

	React.useEffect(() => {
		const walker = doc.createTreeWalker(doc, 0x1);

		const recognisedColours: Set<string> = new Set();
		const pending: { node: HTMLElement | SVGElement, colours: Record<string, string[]> }[] = [];

		for (let node = walker.nextNode() as Element | null; node; node = walker.nextNode() as Element | null)
			if (node instanceof win.HTMLElement || node instanceof win.SVGElement) {
				const styles = win.getComputedStyle(node);
				const colours = {
					background: styles.getPropertyValue('background-color').split(/(?<=\))\s+/),
					color: styles.getPropertyValue('color').split(/(?<=\))\s+/),
					borderColor: styles.getPropertyValue('border-color').split(/(?<=\))\s+/),
				};

				const used: Record<string, string[]> = {background: [], borderColor: [], color: []};

				for (const cat in colours)
					for (const colour of colours[cat as keyof typeof colours])
						if (colour && colour !== 'rgba(0, 0, 0, 0)') {
							recognisedColours.add(colour);
							used[cat].push(colour); // raw value; the variable name isn't known until after sorting
						}

				if (used.background.length || used.borderColor.length || used.color.length)
					pending.push({node, colours: used});
			}

		const sorted = sortColours(recognisedColours);

		// Sorting renumbers everything, so the walk can't assign variable names inline —
		// it has to go through this map, otherwise every element points at the wrong slot.
		const varName = new Map(sorted.map((colour, id) => [colour, `--colour-${id}`]));
		const ref = (colour: string) => `var(${varName.get(colour)})`;

		for (const {node, colours} of pending) {
			void node;
			void colours;
			void ref;
			// if (colours.background.length) node.style.background = colours.background.map(ref).join(' ');
			// if (colours.borderColor.length) node.style.borderColor = colours.borderColor.map(ref).join(' ');
			// if (colours.color.length) node.style.color = colours.color.map(ref).join(' ');
		}

		props.onExtracted(Object.fromEntries(sorted.map((colour, id) => [`--colour-${id}`, colour])));
	}, []);

	return <>
		<div className='dialog-header'>
			<h1>Themenfarben wurden nicht wesentlich erkannt.</h1>
			<p>Es kann sein, dass es sich um ein altes Wireframe handelt...</p>
		</div>

		<div id="colour-list">
			{Object.entries(props.extracted).map(([name, colour]) => (
				<span
					className="colour-preview"
					draggable
					title={colour}
					style={{background: colour}}
					key={name}
					onDragStart={e => {
						e.dataTransfer.setData('application/json', JSON.stringify({colourName: name, colour}));
						e.dataTransfer.setData('text/plain', name);
					}}
				/>
			))}
		</div>
	</>;
}

interface PaletteEditorProps {
	// palette: Palette;
	// onChange: (palette: Palette) => void,
	iframe: HTMLIFrameElement
}

export function PaletteEditor(props: PaletteEditorProps) {
	const doc = React.useMemo(() => props.iframe?.contentDocument!, [props.iframe?.contentDocument]);
	const showExtractionDialog = React.useMemo(() => true, []);

	// Seed from whatever the wireframe already defines; empty string means "not set",
	// which is what isSignificantlyDefined() is counting.
	const [palette, setPalette] = React.useState<Palette>(() => {
		const computed = props.iframe?.contentWindow?.getComputedStyle(doc.documentElement);
		return Object.fromEntries(colourSlots.map(slot =>
			[slot, computed?.getPropertyValue(slot).trim() ?? ''])) as Palette;
	});
	const [extractedColours, setExtractedColours] = React.useState<Record<string, string>>({});
	const [override, setOverride] = React.useState<ColourOverride>({});

	// React.useEffect(() => props.onChange(palette), [palette]);

	const styleEl = useManagedStylesheet(doc, 'wf-palette-vars');

	const cssText = React.useMemo(() => {
		const roleLines = Object.entries(palette)
			.filter(([, value]) => value)
			.map(([name, value]) => `\t${name}: ${value};`);

		const extractedLines = Object.entries(extractedColours).map(
			([name, value]) => `\t${name}: ${value};`
		);

		return `:root {\n${[...roleLines, ...extractedLines].join('\n')}\n}`;
	}, [palette, extractedColours]);

	React.useEffect(() => {
		if (styleEl)
			styleEl.textContent = cssText;
	}, [styleEl, cssText]);

	return <>
		{showExtractionDialog
			? (<ExtractedColours extracted={extractedColours} onExtracted={setExtractedColours} iframe={props.iframe}/>)
			: <label className='dialog-header'>Farbschema anpassen</label>}

		<div id="colour-role-list">
			{Object.entries(palette).map(([name, colour]) => (
				<React.Fragment key={name}>
					{/*{name in override ? <button className='symbolic tertiary unlink-colour-btn' data-icon={'\ue16f'} /> : null}*/}

					<div className="drop-colour"
					     onDragOver={e => e.preventDefault()}
					     onDrop={e => {
							 const payload = JSON.parse(e.dataTransfer.getData('application/json'));
							 setPalette({...palette, [name]: payload.colour});
							 setOverride({...override, [name]: payload.colourName});
						 }}>
						<input
							type="color"
							value={toSwatch(colour)}
							onChange={e => setPalette({
								...palette,
								// Normalise hex back to rgb() so the emitted CSS stays one syntax.
								[name]: serialiseColour(parseColour(e.currentTarget.value)),
							})}
						/>
					</div>
					<code>{name}</code>
				</React.Fragment>
			))}
		</div>

		<div className='buttons'>
			{/* @ts-ignore */}
			<button data-icon={'\ue5ca'} command="close" commandfor='image-picker' type='button'>Ok</button>
		</div>
	</>;
}